import { useMemo } from 'react';
import CONFIG from '../config/config';

/**
 * Detects the country code from the browser's locale
 * @returns A country code (e.g., "US", "PL", "DE") or undefined if not detected
 */
export function detectCountryFromLocale(): string | undefined {
  try {
    // Get locale from browser
    const locale = navigator.language || (navigator as any).userLanguage || 'en-US';
    
    // Extract region code from locale (e.g., 'en-US' -> 'US', 'pl-PL' -> 'PL')
    const regionMatch = locale.match(/-([A-Z]{2})$/i);
    if (regionMatch) {
      const regionCode = regionMatch[1].toUpperCase();
      // Check if this country code exists in our config
      const countryExists = CONFIG.COUNTRY_CODES.some(
        (country) => country.countryCode === regionCode
      );
      if (countryExists) {
        return regionCode;
      }
    }
    
    // Try to extract language code as fallback (e.g., 'pl' from 'pl-PL')
    const languageCode = locale.split('-')[0].toUpperCase();
    const countryFromLanguage = CONFIG.COUNTRY_CODES.find(
      (country) => country.countryCode === languageCode
    );
    if (countryFromLanguage) {
      return countryFromLanguage.countryCode;
    }
  } catch (error) {
    console.warn('Failed to detect country from locale:', error);
  }
  
  return undefined;
}

/**
 * Hook to detect and get the user's country code from browser locale
 * @param defaultCountry - Optional explicit country code to use instead of detection
 * @param fallbackCountry - Optional fallback country code if detection fails (defaults to "PL")
 * @returns The detected or provided country code
 */
export function useCountryDetection(
  defaultCountry?: string,
  fallbackCountry: string = 'PL'
): string {
  return useMemo(() => {
    // Priority: 1. explicit defaultCountry, 2. detected from locale, 3. fallback
    if (defaultCountry) {
      const upperCountry = defaultCountry.toUpperCase();
      const countryExists = CONFIG.COUNTRY_CODES.some(
        (country) => country.countryCode === upperCountry
      );
      if (countryExists) {
        return upperCountry;
      }
    }
    
    const detected = detectCountryFromLocale();
    if (detected) {
      return detected;
    }
    
    return fallbackCountry;
  }, [defaultCountry, fallbackCountry]);
}

